import { useEffect, useState } from 'react';
import { Box, Button, Stack } from '@mui/material';
import type { AllocationPreviewView, ChargeView, SupportedLocale } from '@stm/contracts';
import { translate } from '@stm/i18n';
import { AllocationLines } from './AllocationLines';
import { apiRequest } from './api';
import {
  draftLinesFromPreview,
  evaluateAllocationDraft,
  type AllocationDraftLine,
} from './billing';
import { errorKeyOf, type ErrorKey } from './BillingForms';

export interface AllocateCreditFormProps {
  locale: SupportedLocale;
  /** `/api/v1/organizations/{id}/billing`. */
  base: string;
  accountId: string;
  paymentId: string;
  /** The retained value of the payment that is still neither allocated nor refunded. */
  availableMinor: string;
  currency: string;
  charges: readonly ChargeView[];
  csrfToken: string;
  onDone: () => Promise<void>;
  onCancel: () => void;
  onError: (key: ErrorKey | null) => void;
}

/**
 * Allocates the unallocated credit of a payment that is already posted. The server proposes the
 * oldest-due-first allocation of the available amount; finance may change the amounts before
 * confirming. The form is mounted per attempt, so its idempotency key is stable across retries.
 */
export function AllocateCreditForm({
  locale,
  base,
  accountId,
  paymentId,
  availableMinor,
  currency,
  charges,
  csrfToken,
  onDone,
  onCancel,
  onError,
}: AllocateCreditFormProps): React.JSX.Element | null {
  const [draft, setDraft] = useState<AllocationDraftLine[] | null>(null);
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const evaluation = draft === null ? null : evaluateAllocationDraft(availableMinor, draft);

  useEffect(() => {
    let cancelled = false;
    apiRequest<AllocationPreviewView>(
      `${base}/accounts/${accountId}/allocation-preview?amountMinor=${availableMinor}`,
    ).then(
      (proposal) => {
        if (cancelled) return;
        const proposed = draftLinesFromPreview(proposal);
        const proposedIds = new Set(proposed.map((line) => line.chargeId));
        // Every other open charge is offered with an empty amount, so finance can redirect credit.
        const others = charges
          .filter((charge) => !charge.reversed && BigInt(charge.outstandingMinor) > 0n)
          .filter((charge) => !proposedIds.has(charge.id))
          .sort((left, right) => left.dueOn.localeCompare(right.dueOn))
          .map((charge) => ({ chargeId: charge.id, amountText: '' }));
        setDraft([...proposed, ...others]);
      },
      (error: unknown) => {
        if (!cancelled) onError(errorKeyOf(error));
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  async function submit(): Promise<void> {
    if (evaluation?.problem !== null || evaluation.lines.length === 0) return;
    onError(null);
    try {
      await apiRequest(`${base}/payments/${paymentId}/allocations`, {
        method: 'POST',
        body: { allocations: evaluation.lines },
        csrfToken,
        idempotencyKey,
      });
      await onDone();
    } catch (error) {
      onError(errorKeyOf(error));
    }
  }

  if (draft === null || evaluation === null) return null;
  const nothingOpen = draft.length === 0;

  return (
    <Stack
      component="form"
      spacing={1}
      sx={{ py: 1 }}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      {nothingOpen ? (
        <Box role="status">{translate(locale, 'billing.payment.allocate.nothingOpen')}</Box>
      ) : (
        <AllocationLines
          locale={locale}
          currency={currency}
          charges={charges}
          draft={draft}
          evaluation={evaluation}
          titleId={`allocate-title-${paymentId}`}
          onChange={setDraft}
        />
      )}
      <Stack direction="row" spacing={1}>
        <Button
          type="submit"
          size="small"
          variant="contained"
          disabled={evaluation.problem !== null || evaluation.lines.length === 0}
        >
          {translate(locale, 'billing.payment.allocate.submit')}
        </Button>
        <Button size="small" onClick={onCancel}>
          {translate(locale, 'billing.cancel')}
        </Button>
      </Stack>
    </Stack>
  );
}
